import { ErrorState, Loading, PageHead, useFormat } from '@ly360/ui';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';

interface UserRow {
  id: string;
  email: string;
  displayName: string;
  displayNameAr: string | null;
  locale: string;
  status: 'active' | 'invited' | 'disabled';
  lastLoginAt: string | null;
  roles: string[];
}
interface Role {
  key: string;
  nameEn: string;
  nameAr: string;
}

export function UsersPage() {
  const { t, i18n } = useTranslation();
  const fmt = useFormat();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<{ items: UserRow[] }>('/v1/users') });
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api<{ items: Role[] }>('/v1/roles') });
  const ar = i18n.language === 'ar';
  const roleName = (key: string) => {
    const r = roles.data?.items.find((x) => x.key === key);
    return r ? (ar ? r.nameAr : r.nameEn) : key;
  };

  return (
    <>
      <PageHead eyebrow={t('nav.admin')} title={t('users.title')} subtitle={t('users.subtitle')} />
      <section className="card">
        {users.isLoading && <Loading />}
        {users.isError && <ErrorState error={users.error} onRetry={() => void users.refetch()} />}
        {users.data && (
          <div className="table-wrap">
            <table className="table table-responsive">
              <thead>
                <tr>
                  <th>{t('users.col.name')}</th>
                  <th>{t('users.col.email')}</th>
                  <th>{t('users.col.roles')}</th>
                  <th>{t('users.col.status')}</th>
                  <th>{t('users.col.lastLogin')}</th>
                </tr>
              </thead>
              <tbody>
                {users.data.items.map((u) => {
                  const name = ar && u.displayNameAr ? u.displayNameAr : u.displayName;
                  return (
                    <tr key={u.id}>
                      <td data-label={t('users.col.name')}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span
                            className="avatar"
                            style={{ width: 30, height: 30, fontSize: '0.7rem' }}
                            aria-hidden="true"
                          >
                            {name
                              .split(/\s+/)
                              .map((w) => w[0])
                              .slice(0, 2)
                              .join('')}
                          </span>
                          <strong dir="auto">{name}</strong>
                        </div>
                      </td>
                      <td
                        className="mono"
                        style={{ fontSize: 'var(--t-xs)' }}
                        data-label={t('users.col.email')}
                      >
                        {u.email}
                      </td>
                      <td data-label={t('users.col.roles')}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {u.roles.map((r) => (
                            <span key={r} className="badge">
                              {roleName(r)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td data-label={t('users.col.status')}>
                        <span
                          className={`badge ${u.status === 'active' ? 'badge-success' : 'badge-neutral'}`}
                        >
                          {t(`users.status.${u.status}`)}
                        </span>
                      </td>
                      <td className="faint" data-label={t('users.col.lastLogin')}>
                        {u.lastLoginAt ? fmt.relative(u.lastLoginAt) : t('users.never')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
